import { Button, DatePicker, Dialog } from '@neo4j-ndl/react';
import type { TimeRange } from '@nx/state';
import React, { useEffect, useMemo, useState } from 'react';

const MAX_DAYS_BACK = 30;

export type TimePickerDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (timeRange: TimeRange) => void;
  initialTimeRange?: TimeRange;
};

const TimePickerDialog: React.FC<TimePickerDialogProps> = ({ isOpen, onClose, onApply, initialTimeRange }) => {
  const defaultTimeRange: TimeRange = useMemo(
    () =>
      initialTimeRange ?? {
        startTime: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
        endTime: new Date(),
      },
    [initialTimeRange],
  );

  const [localStartTime, setLocalStartTime] = useState<Date>(defaultTimeRange.startTime);
  const [localEndTime, setLocalEndTime] = useState<Date>(defaultTimeRange.endTime);

  // Reset local state when dialog opens with new initial values
  useEffect(() => {
    if (isOpen) {
      setLocalStartTime(defaultTimeRange.startTime);
      setLocalEndTime(defaultTimeRange.endTime);
    }
  }, [isOpen, defaultTimeRange]);

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setLocalStartTime(date);
      // If the new start date is after the current end date, reset end date to now
      if (date > localEndTime) {
        setLocalEndTime(new Date());
      }
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date && date >= localStartTime && date <= new Date()) {
      setLocalEndTime(date);
    }
  };

  const handleApply = () => {
    const timeRange: TimeRange = {
      startTime: localStartTime,
      endTime: localEndTime,
    };
    onApply(timeRange);
    onClose();
  };

  const handleCancel = () => {
    // Reset to initial values
    setLocalStartTime(defaultTimeRange.startTime);
    setLocalEndTime(defaultTimeRange.endTime);
    onClose();
  };

  const minStartDate = new Date();
  minStartDate.setDate(minStartDate.getDate() - MAX_DAYS_BACK);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="small">
      <Dialog.Header>Select custom time range</Dialog.Header>

      <Dialog.Content>
        <div className="flex flex-col gap-4">
          <DatePicker
            reactDatePickerProps={{
              selected: localStartTime,
              onChange: handleStartDateChange,
              popperProps: {
                strategy: 'fixed',
              },
              calendarStartDay: 1,
              minDate: minStartDate,
              maxDate: new Date(),
              dateFormat: 'yyyy-MM-dd HH:mm',
              showTimeInput: true,
              shouldCloseOnSelect: false,
            }}
            textInputProps={{
              isFluid: true,
              label: 'From',
            }}
            timePickerProps={{
              timeInterval: 15,
            }}
          />
          <DatePicker
            reactDatePickerProps={{
              selected: localEndTime,
              onChange: handleEndDateChange,
              popperProps: {
                strategy: 'fixed',
              },
              calendarStartDay: 1,
              minDate: localStartTime,
              maxDate: new Date(),
              dateFormat: 'yyyy-MM-dd HH:mm',
              showTimeInput: true,
              shouldCloseOnSelect: false,
            }}
            textInputProps={{
              isFluid: true,
              label: 'To',
            }}
            timePickerProps={{
              timeInterval: 15,
            }}
          />
        </div>
      </Dialog.Content>

      <Dialog.Actions>
        <div className="flex justify-end gap-2">
          <Button fill="outlined" onClick={handleCancel} aria-label="Cancel time range selection">
            Cancel
          </Button>
          <Button fill="filled" onClick={handleApply} aria-label="Apply selected time range">
            Apply
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default TimePickerDialog;
